import React, { useState, useEffect } from "react";
import { getDocs, collection, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from '../config/firebaseConfig'; // Firebase config'un doğru ayarlandığını varsayıyorum
import { Button, Modal, Form, Table, Dropdown, DropdownButton, InputGroup, FormControl, Pagination } from 'react-bootstrap';

const AfisDuzenle = () => {
  const [afisler, setAfisler] = useState([]);
  const [filteredAfisler, setFilteredAfisler] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [editModalShow, setEditModalShow] = useState(false);
  const [selectedAfis, setSelectedAfis] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const afislerPerPage = 8; // Her sayfada 8 afiş

  // Firestore'dan afişleri çek ve ters sırala (en son eklenen en üstte)
  useEffect(() => {
    const fetchAfisler = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "gallery"));
        const afisList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate().toLocaleString() || 'Unknown Date',
          createdAtRaw: doc.data().createdAt?.toDate() || new Date(0), // Sıralama için ham tarih
        }));

        // En son eklenen en üstte olacak şekilde ters sıralama
        afisList.sort((a, b) => b.createdAtRaw - a.createdAtRaw);

        setAfisler(afisList);
        setFilteredAfisler(afisList);

        // Benzersiz kategorileri çıkar
        const uniqueCategories = ['all', ...new Set(afisList.map(afis => afis.category))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching afisler:", error);
      }
    };

    fetchAfisler();
  }, []);

  // Filtreleme ve arama
  useEffect(() => {
    let filtered = afisler;

    // Kategoriye göre filtreleme
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(afis => afis.category === selectedCategory);
    }

    // Arama terimine göre filtreleme
    if (searchTerm) {
      filtered = filtered.filter(afis =>
        afis.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        afis.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        afis.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAfisler(filtered);
    setCurrentPage(1); // Filtreleme veya arama yapıldığında sayfayı sıfırla
  }, [selectedCategory, searchTerm, afisler]);

  // Sayfalama için afişleri dilimleme
  const indexOfLastAfis = currentPage * afislerPerPage;
  const indexOfFirstAfis = indexOfLastAfis - afislerPerPage;
  const currentAfisler = filteredAfisler.slice(indexOfFirstAfis, indexOfLastAfis);

  // Pagination öğelerini oluşturma
  const totalPages = Math.ceil(filteredAfisler.length / afislerPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageItems = [];
  for (let i = 1; i <= totalPages; i++) {
    pageItems.push(
      <Pagination.Item
        key={i}
        active={i === currentPage}
        onClick={() => paginate(i)}
      >
        {i}
      </Pagination.Item>
    );
  }

  // Afiş düzenleme
  const handleEdit = (afis) => {
    setSelectedAfis(afis);
    setEditModalShow(true);
  };

  const saveEdit = async () => {
    if (!selectedAfis) return;

    try {
      const afisRef = doc(db, "gallery", selectedAfis.id);
      await updateDoc(afisRef, {
        title: selectedAfis.title,
        description: selectedAfis.description,
        category: selectedAfis.category,
      });
      setAfisler(afisler.map(a => (a.id === selectedAfis.id ? selectedAfis : a)));
      setEditModalShow(false);
      alert("Afiş başarıyla güncellendi!");
    } catch (error) {
      console.error("Error updating afis:", error);
      alert("Afiş güncellenirken hata oluştu: " + error.message);
    }
  };

  // Afiş silme
  const handleDelete = async (id) => {
    if (window.confirm("Bu afişi silmek istediğinize emin misiniz?")) {
      try {
        await deleteDoc(doc(db, "gallery", id));
        setAfisler(afisler.filter(afis => afis.id !== id));
        alert("Afiş başarıyla silindi!");
        // Silme sonrası sayfa kontrolü
        if (currentAfisler.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (error) {
        console.error("Error deleting afis:", error);
        alert("Afiş silinirken hata oluştu: " + error.message);
      }
    }
  };

  // Authentication check
  onAuthStateChanged(getAuth(), (user) => {
    if (!user) {
      window.location.href = "/login";
    }
  });

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">Afiş Düzenleme</h2>

      {/* Arama ve Filtreleme */}
      <div className="d-flex justify-content-between mb-4 flex-wrap gap-3">
        <InputGroup style={{ maxWidth: '300px' }}>
          <FormControl
            placeholder="Afiş ara (başlık, açıklama, kategori)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <DropdownButton
          id="category-filter"
          title={`Kategori: ${selectedCategory === 'all' ? 'Tümü' : selectedCategory}`}
          variant="outline-primary"
        >
          {categories.map((cat) => (
            <Dropdown.Item
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              active={cat === selectedCategory}
            >
              {cat === 'all' ? 'Tümü' : cat}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </div>

      {/* Afiş Tablosu */}
      <Table striped bordered hover responsive>
        <thead className="table-dark">
          <tr>
            <th>Görsel</th>
            <th>Başlık</th>
            <th>Açıklama</th>
            <th>Kategori</th>
            <th>Oluşturulma Tarihi</th>
            <th>İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {currentAfisler.length > 0 ? (
            currentAfisler.map((afis) => (
              <tr key={afis.id}>
                <td>
                  <img src={afis.imageUrl} alt={afis.title} style={{ width: '100px', height: 'auto' }} />
                </td>
                <td>{afis.title}</td>
                <td>{afis.description}</td>
                <td>{afis.category}</td>
                <td>{afis.createdAt}</td>
                <td>
                  <Button
                    variant="warning"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEdit(afis)}
                  >
                    Düzenle
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(afis.id)}
                  >
                    Sil
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center">Afiş bulunamadı.</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.Prev
              onClick={() => setCurrentPage(prev => (prev > 1 ? prev - 1 : prev))}
              disabled={currentPage === 1}
            />
            {pageItems}
            <Pagination.Next
              onClick={() => setCurrentPage(prev => (prev < totalPages ? prev + 1 : prev))}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Düzenleme Modal */}
      <Modal show={editModalShow} onHide={() => setEditModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Afiş Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAfis && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Başlık</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedAfis.title}
                  onChange={(e) => setSelectedAfis({ ...selectedAfis, title: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Açıklama</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={selectedAfis.description}
                  onChange={(e) => setSelectedAfis({ ...selectedAfis, description: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Kategori</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedAfis.category}
                  onChange={(e) => setSelectedAfis({ ...selectedAfis, category: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Görsel URL</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedAfis.imageUrl}
                  disabled
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditModalShow(false)}>
            Kapat
          </Button>
          <Button variant="primary" onClick={saveEdit}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AfisDuzenle;